import React, { useEffect, useState } from "react";
import YhInput from "../../../../../../ui/YhInput";
import YhSelect from "../../../../../../ui/YhSelect";
import YhButton from "../../../../../../ui/YhButton";
import {
  openErrorNotification,
} from "../../../../../../helpers/methods";
import {
  addDeveloper,
  addProject,
  getDeveloper,
  updateProject,
} from "../../../../../Project/components/stepper/StepperService";
import { getLocationMeta } from "../ProjectLocationList/projectLocationService";
import notifiers from "../../../../../../global/constants/NotificationConstants";
import { uploadDeveloperImage } from "./DeveloperService";

interface Errors {
  developer_name?: string;
  project_name?: string;
}

const AddDeveloper = ({
  developerData = {},
  setDeveloperData,
  closeDrawer,
}: any) => {
  const [errors, setErrors] = useState<Errors>({});
  const [countryList, setCountryList] = useState<any[]>([]);
  const [allDeveloperData, setAllDeveloperData] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isEdit = !!developerData?._id;
  const isProjectMode = developerData.isProject === 1;

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [developerData?.countryId]);

  const init = async () => {
    try {
      const locationRes = await getLocationMeta();
      const countryOptions = (locationRes.data || []).map((country: any) => ({
        value: country._id,
        label: country.country_name,
      }));
      setCountryList(countryOptions);

      if (developerData?.countryId) {
        const devRes = await getDeveloper(developerData.countryId);
        const formattedDevelopers = (devRes.data || []).map((dev: any) => ({
          label: dev.developer_name,
          value: dev._id,
        }));
        setAllDeveloperData(formattedDevelopers);
      } else {
        setAllDeveloperData([]);
      }
    } catch (error: any) {
      openErrorNotification(error.message || notifiers.GENERIC_ERROR);
    }
  };

  const updateField = (patch: Record<string, any>) => {
    setDeveloperData((prev: any) => ({ ...prev, ...patch }));
  };

  const handleAddFaq = () => {
    updateField({
      faq: [...(developerData.faq || []), { question: "", answer: "" }],
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      const data = new FormData();
      data.append("files", files[0]);
      const url: string | string[] = await uploadDeveloperImage(
        data,
        developerData.developer_name
      );
      const imageUrl = Array.isArray(url) ? url[0] : url;
      updateField({ image: imageUrl });
    } catch {
      openErrorNotification("Upload failed");
    }
  };

  const handleSubmit = async () => {
    try {
      if (isProjectMode && !developerData.project_name) {
        setErrors((e) => ({ ...e, project_name: "Project Name is required" }));
        return;
      }
      if (!isProjectMode && !developerData.developer_name) {
        setErrors((e) => ({
          ...e,
          developer_name: "Developer Name is required",
        }));
        return;
      }

      setSubmitting(true);

      const payload = isProjectMode
        ? {
            developerId: developerData?.developerId,
            project_name: developerData.project_name,
            countryId: developerData.countryId,
            countryName: developerData.countryName,
            _id: developerData._id,
          }
        : {
            developer_name: developerData.developer_name,
            countryId: developerData.countryId,
            countryName: developerData.countryName,
            description: developerData.description,
            faq: developerData.faq,
            image: developerData?.image,
            _id: developerData._id,
          };

      const res = isProjectMode
        ? developerData._id
          ? await updateProject(payload)
          : await addProject(payload)
        : await addDeveloper(payload);

      if (res.success === 1) {
        closeDrawer();
      }
    } catch (err) {
      console.error("Submit Error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const title = isEdit
    ? isProjectMode
      ? "Edit Project"
      : "Edit Developer"
    : isProjectMode
      ? "Create Project"
      : "Create Developer";

  return (
    <div
      className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4"
      onClick={closeDrawer}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-[#EEF0F5] px-5 py-4">
          <h2 className="text-lg font-bold text-[#111827]">{title}</h2>
          <button
            type="button"
            onClick={closeDrawer}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="mb-4 flex items-center gap-3">
            <label className="relative inline-flex h-6 w-11 cursor-pointer items-center">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={!!isProjectMode}
                disabled={isEdit}
                onChange={(e) => {
                  updateField({ isProject: e.target.checked ? 1 : 0 });
                  setErrors({});
                }}
              />
              <span className="absolute inset-0 rounded-full bg-[#D1D5DB] transition peer-checked:bg-[#A02448] peer-disabled:opacity-50" />
              <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5" />
            </label>
            <span className="text-sm font-medium text-[#374151]">
              {isProjectMode ? "Project" : "Developer"}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <YhSelect
              label="Country Name"
              placeholder="Select Country"
              value={developerData.countryId || ""}
              options={countryList}
              onChange={(e) => {
                const selected = countryList.find(
                  (c) => String(c.value) === e.target.value
                );
                updateField({
                  countryId: e.target.value,
                  countryName: selected?.label || "",
                  developerId: "",
                });
              }}
            />

            {isProjectMode ? (
              <>
                <YhSelect
                  label="Developer Name"
                  placeholder="Select Developer"
                  value={developerData.developerId || ""}
                  options={allDeveloperData}
                  disabled={!developerData.countryId || developerData.edit}
                  onChange={(e) => {
                    const selected = allDeveloperData.find(
                      (d) => String(d.value) === e.target.value
                    );
                    updateField({
                      developerId: e.target.value,
                      developer_name: selected?.label || "",
                    });
                  }}
                />

                <YhInput
                  label="Project Name"
                  requiredMark
                  placeholder="Project Name"
                  value={developerData.project_name ?? ""}
                  error={errors.project_name}
                  onChange={(e) => {
                    updateField({ project_name: e.target.value });
                    if (errors.project_name) {
                      setErrors((prev) => ({ ...prev, project_name: "" }));
                    }
                  }}
                />
              </>
            ) : (
              <>
                <YhInput
                  label="Developer Name"
                  requiredMark
                  placeholder="Developer Name"
                  value={developerData?.developer_name ?? ""}
                  error={errors.developer_name}
                  onChange={(e) => {
                    updateField({ developer_name: e.target.value });
                    if (errors.developer_name) {
                      setErrors((prev) => ({ ...prev, developer_name: "" }));
                    }
                  }}
                />

                <div>
                  <label className="yh-label">Description</label>
                  <textarea
                    className="yh-input min-h-[100px] resize-y"
                    placeholder="Description"
                    value={developerData?.description || ""}
                    onChange={(e) =>
                      updateField({ description: e.target.value })
                    }
                  />
                </div>

                <div className="rounded-xl border border-[#EEF0F5] p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-[#111827]">
                      Frequently Ask Question
                    </p>
                    <YhButton variant="secondary" size="sm" onClick={handleAddFaq}>
                      Add More
                    </YhButton>
                  </div>

                  {(developerData?.faq || []).map((item: any, index: number) => (
                    <div
                      key={index}
                      className="mb-3 grid grid-cols-1 gap-3 border-b border-[#EEF0F5] pb-3 last:mb-0 last:border-0 last:pb-0"
                    >
                      <YhInput
                        label="Question"
                        placeholder="Question"
                        value={item.question ?? ""}
                        onChange={(e) => {
                          const updatedFaq = [...(developerData?.faq || [])];
                          updatedFaq[index] = {
                            ...updatedFaq[index],
                            question: e.target.value,
                          };
                          updateField({ faq: updatedFaq });
                        }}
                      />
                      <YhInput
                        label="Answer"
                        placeholder="Enter"
                        value={item.answer ?? ""}
                        onChange={(e) => {
                          const updatedFaq = [...(developerData?.faq || [])];
                          updatedFaq[index] = {
                            ...updatedFaq[index],
                            answer: e.target.value,
                          };
                          updateField({ faq: updatedFaq });
                        }}
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="yh-label">Image</label>
                  <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-[#D1D5DB] bg-[#F9FAFB] px-4 py-8 text-center hover:border-[#A02448] hover:bg-[#F5F6FF]">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#A02448"
                      strokeWidth="1.5"
                      className="mb-2"
                    >
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <path d="M17 8l-5-5-5 5" />
                      <path d="M12 3v12" />
                    </svg>
                    <span className="text-sm text-[#6B7280]">
                      Click or drag file to this area to upload
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </label>

                  {developerData.image ? (
                    <div className="relative mt-3 inline-block h-24 w-24 overflow-hidden rounded-lg border border-[#EEF0F5]">
                      <img
                        src={developerData.image}
                        alt="Developer"
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => updateField({ image: "" })}
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs text-white"
                        aria-label="Remove image"
                      >
                        ✕
                      </button>
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-3 border-t border-[#EEF0F5] bg-[#FAFBFC] px-5 py-4">
          <YhButton variant="secondary" size="sm" onClick={closeDrawer}>
            Cancel
          </YhButton>
          <YhButton
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Submit"}
          </YhButton>
        </div>
      </div>
    </div>
  );
};

export default AddDeveloper;
